/**
 * Generic Dashboard Hook
 *
 * Provides data fetching with caching for any dashboard type
 */

import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { reportingService } from '@/services/reporting.service';
import type {
  AdminOverviewRequest,
  AdminOverviewResponse,
  ExecutiveSummaryRequest,
  ExecutiveSummaryResponse,
  ManagerTeamRequest,
  ManagerTeamResponse,
  LearnerProgressRequest,
  LearnerProgressResponse,
  DepartmentPerformanceRequest,
  DepartmentPerformanceResponse,
  CourseAnalyticsRequest,
  CourseAnalyticsResponse,
} from '@/types/reporting-v2';

type DashboardType =
  | 'admin-overview'
  | 'executive-summary'
  | 'manager-team'
  | 'learner-progress'
  | 'department-performance'
  | 'course-analytics';

type DashboardRequest<T extends DashboardType> = T extends 'admin-overview'
  ? AdminOverviewRequest
  : T extends 'executive-summary'
  ? ExecutiveSummaryRequest
  : T extends 'manager-team'
  ? ManagerTeamRequest
  : T extends 'learner-progress'
  ? LearnerProgressRequest
  : T extends 'department-performance'
  ? DepartmentPerformanceRequest
  : T extends 'course-analytics'
  ? CourseAnalyticsRequest
  : never;

type DashboardResponse<T extends DashboardType> = T extends 'admin-overview'
  ? AdminOverviewResponse['data']
  : T extends 'executive-summary'
  ? ExecutiveSummaryResponse['data']
  : T extends 'manager-team'
  ? ManagerTeamResponse['data']
  : T extends 'learner-progress'
  ? LearnerProgressResponse['data']
  : T extends 'department-performance'
  ? DepartmentPerformanceResponse['data']
  : T extends 'course-analytics'
  ? CourseAnalyticsResponse['data']
  : never;

const dashboardFetchers = {
  'admin-overview': reportingService.getAdminOverview.bind(reportingService),
  'executive-summary': reportingService.getExecutiveSummary.bind(reportingService),
  'manager-team': reportingService.getManagerTeam.bind(reportingService),
  'learner-progress': reportingService.getLearnerProgress.bind(reportingService),
  'department-performance': reportingService.getDepartmentPerformance.bind(reportingService),
  'course-analytics': reportingService.getCourseAnalytics.bind(reportingService),
};

export function useDashboard<T extends DashboardType>(
  type: T,
  params: DashboardRequest<T>,
  options?: Omit<UseQueryOptions<DashboardResponse<T>>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: ['dashboard', type, params],
    queryFn: () => {
      const fetcher = dashboardFetchers[type] as (params: any) => Promise<DashboardResponse<T>>;
      return fetcher(params);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes (respects API cache)
    refetchOnWindowFocus: true,
    ...options,
  });
}
